export type ApiErrorPayload = {
  statusCode: number;
  errorCode?: string;
  errorDetailCode?: string;
  message?: string;
  cycleTaskNumbers?: number[];
  blockedTaskId?: string;
  blockedTaskNumber?: number;
  blockedTaskTitle?: string;
  blockedEngine?: string;
  blockedModel?: string;
  // 서버 응답 헤더(x-request-id)의 불투명 요청 식별자. 최종 결과 보고가 서버 관측과
  // 같은 사건으로 묶일 때만 쓰고, 화면에는 노출하지 않는다.
  requestId?: string;
};

export class ApiError extends Error {
  public readonly statusCode: number;
  public readonly errorCode?: string;
  public readonly errorDetailCode?: string;
  public readonly apiMessage?: string;
  public readonly cycleTaskNumbers?: number[];
  public readonly blockedTaskId?: string;
  public readonly blockedTaskNumber?: number;
  public readonly blockedTaskTitle?: string;
  public readonly blockedEngine?: string;
  public readonly blockedModel?: string;
  public readonly requestId?: string;

  constructor(payload: ApiErrorPayload, fallbackMessage?: string) {
    const message =
      typeof payload.message === 'string' && payload.message.trim().length > 0
        ? payload.message
        : fallbackMessage && fallbackMessage.trim().length > 0
          ? fallbackMessage
          : `HTTP ${payload.statusCode}`;

    super(message);
    this.name = 'ApiError';
    this.statusCode = payload.statusCode;
    this.errorCode = payload.errorCode;
    this.errorDetailCode = payload.errorDetailCode;
    this.apiMessage = payload.message;
    this.cycleTaskNumbers = payload.cycleTaskNumbers;
    this.blockedTaskId = payload.blockedTaskId;
    this.blockedTaskNumber = payload.blockedTaskNumber;
    this.blockedTaskTitle = payload.blockedTaskTitle;
    this.blockedEngine = payload.blockedEngine;
    this.blockedModel = payload.blockedModel;
    this.requestId = payload.requestId;
  }
}

export class TimeoutError extends Error {
  constructor() {
    super('Request timed out');
    this.name = 'TimeoutError';
  }
}

export class AuthRedirectError extends Error {
  constructor() {
    super('');
    this.name = 'AuthRedirectError';
  }
}

export class RetryableAuthError extends Error {
  public readonly newToken: string;
  constructor(newToken: string) {
    super('Token refreshed, retry request');
    this.name = 'RetryableAuthError';
    this.newToken = newToken;
  }
}

export class RefreshUnauthorizedError extends Error {
  // 서버가 확인한 종료 사유(예: 세션 제한 축출)만 담는다. 알 수 없으면 undefined.
  public readonly reason?: string;
  constructor(reason?: string) {
    super('Refresh token is no longer valid');
    this.name = 'RefreshUnauthorizedError';
    this.reason = reason;
  }
}

export class RefreshTransientError extends Error {
  constructor(message = 'Refresh failed due to a transient error') {
    super(message);
    this.name = 'RefreshTransientError';
  }
}

// Portal은 별도의 ApiError 클래스를 유지하므로 instanceof만으로는 같은 오류를
// 알아보지 못한다. 이름·상태 코드 모양으로 판정해 호스트 구현을 가리지 않는다.
export const isApiError = (error: unknown): error is ApiError => {
  if (error instanceof ApiError) return true;
  if (typeof error !== 'object' || error === null) return false;
  const candidate = error as { name?: unknown; statusCode?: unknown };
  return candidate.name === 'ApiError' && typeof candidate.statusCode === 'number';
};

// 서버 관측과 묶을 불투명 요청 ID를 오류에 싣는다. 본문·토큰 같은 정보는 절대 싣지 않는다.
export const withServerRequestId = <T extends Error>(error: T, requestId: string | undefined): T => {
  if (requestId) {
    (error as Error & { requestId?: string }).requestId = requestId;
  }
  return error;
};

export const readErrorServerRequestId = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }
  const requestId = (error as { requestId?: unknown }).requestId;
  return typeof requestId === 'string' && requestId.length > 0 ? requestId : undefined;
};

export function getApiErrorDetailMessageKey(error: unknown): string | null {
  if (isApiError(error) && typeof error.errorDetailCode === 'string' && error.errorDetailCode.trim().length > 0) {
    return `apiErrorDetails.${error.errorDetailCode.trim()}`;
  }

  return null;
}

export function getApiErrorMessageKey(error: unknown): string {
  if (error instanceof AuthRedirectError) {
    return 'apiErrors.AUTH_REDIRECT';
  }

  if (error instanceof TimeoutError) {
    return 'apiErrors.REQUEST_TIMEOUT';
  }

  if (isApiError(error)) {
    if (typeof error.errorCode === 'string' && error.errorCode.trim().length > 0) {
      return `apiErrors.${error.errorCode.trim()}`;
    }

    switch (error.statusCode) {
      case 400:
      case 401:
      case 403:
      case 404:
      case 409:
      case 429:
      case 500:
      case 503:
        return `apiErrors.HTTP_${error.statusCode}`;
      default:
        return 'apiErrors.UNKNOWN';
    }
  }

  return 'apiErrors.UNKNOWN';
}

export function shouldSuppressErrorToast(error: unknown): boolean {
  if (error && typeof error === 'object' && 'name' in error) {
    const name = String((error as { name?: unknown }).name);
    if (name === 'AbortError') return true;
  }

  return error instanceof AuthRedirectError || error instanceof RetryableAuthError;
}
